import { useEffect, useState } from 'react';
import { BlogTopicApi } from '../api/adminApi';
import { deriveTopicsFromBlogs, fallbackBlogs as fallbackBlogEntries, shapeTopic } from './blogHelpers';

let cachedTopics = null;
let inflight = null;
let cachedFallback = false;

const fetchTopics = async () => {
  if (cachedTopics) {
    return { data: cachedTopics, isFallback: cachedFallback };
  }
  if (inflight) {
    return inflight;
  }
  inflight = BlogTopicApi.getAll()
    .then((data) => {
      if (Array.isArray(data) && data.length) {
        cachedTopics = data.map(shapeTopic);
        cachedFallback = false;
      } else {
        cachedTopics = deriveTopicsFromBlogs(fallbackBlogEntries);
        cachedFallback = true;
      }
      return { data: cachedTopics, isFallback: cachedFallback };
    })
    .catch(() => {
      cachedTopics = deriveTopicsFromBlogs(fallbackBlogEntries);
      cachedFallback = true;
      return { data: cachedTopics, isFallback: cachedFallback };
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

export const useBlogTopics = (blogs = []) => {
  const [topics, setTopics] = useState(cachedTopics || deriveTopicsFromBlogs(blogs) || []);
  const [isLoading, setIsLoading] = useState(!cachedTopics);
  const [isFallback, setIsFallback] = useState(cachedFallback);

  useEffect(() => {
    let isMounted = true;

    if (cachedTopics) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    fetchTopics()
      .then(({ data, isFallback: fallbackFlag }) => {
        if (!isMounted) return;
        setTopics(data);
        setIsFallback(fallbackFlag);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!cachedTopics && Array.isArray(blogs) && blogs.length) {
      setTopics(deriveTopicsFromBlogs(blogs));
    }
  }, [blogs]);

  return { topics, isLoading, isFallback };
};

export const primeBlogTopicCache = (topics) => {
  if (!Array.isArray(topics)) return;
  cachedTopics = topics.map(shapeTopic);
  cachedFallback = false;
};

export const invalidateBlogTopicCache = () => {
  cachedTopics = null;
  cachedFallback = false;
};
